import React, { useState, useEffect, useMemo, useRef, startTransition, useCallback } from 'react';
import { format, addMinutes, parse, addYears, startOfDay, startOfMonth } from 'date-fns';
import { useLocation, useParams } from 'react-router-dom';
import '../styles/BookingFlow.css';
import {
  slotFitsEmployeeSchedule,
} from '../../shared/booking/employee-schedule/workingSchedule';
import { useStorefrontAuth } from '../auth/context/StorefrontAuthContext';
import {
  resolveStorefrontPaymentGateways,
  storefrontHasOnlineCheckout,
  computeStorefrontBookingTotal,
} from '../payments/storefront-booking/resolveStorefrontPaymentGateways';
import StorefrontPaymentStep from '../payments/storefront-booking/StorefrontPaymentStep';
import '../payments/storefront-booking/storefront-booking-payment.css';
import { useDepositSettings } from '../../components/settings/deposits/useDepositSettings';
import DepositCancellationPolicy, {
  hasCancellationPolicyContent,
} from '../shared/deposit/DepositCancellationPolicy';
import StorefrontLoginPanel from '../auth/components/StorefrontLoginPanel';
import StorefrontBookingCalendar from '../booking-calendar/StorefrontBookingCalendar';
import '../styles/BookingModal.css';
import '../auth/styles/StorefrontLoginModal.css';
import TimeSelectionStep from '../storefront-booking/time-selection/TimeSelectionStep';
import StorefrontStaffSelectionGrid from '../storefront-staff-selection/StorefrontStaffSelectionGrid';
import {
  getDoubleBookingSettings,
  isAnyEmployeeSlotAvailable,
  isEmployeeSlotAvailable,
} from '../../shared/booking/double-booking/employeeSlotAvailability';
import AvailabilityService from '../storefront-client-portal/bundle/components/booking/services/AvailabilityService';
import { createPublicClient } from '../../api/companyRecords';
import { createStorefrontAppointment } from '../../api/storefront/storefront';
import { auth } from '../../auth/clientAppAuth';
import {
  buildStorefrontDayAppointmentsKey,
  buildStorefrontStaffCacheKey,
  computeStorefrontTotalDurationMinutes,
  getCachedStorefrontStaffPrefetch,
  prefetchStorefrontAvailability,
  prefetchStorefrontAvailabilityForToday,
} from '../storefront-booking/prefetchStorefrontAvailability';
import { warmStorefrontBusySlotsForDate } from '../storefront-booking/warmStorefrontBusySlots';
import { getDefaultAppointmentStatus } from '../../utils/appointmentWorkflow';
import useCompanyLocations from '../../locations/hooks/useCompanyLocations';
import LocationPicker from '../../locations/components/LocationPicker';
import { buildAppointmentLocationFields } from '../../locations/filterByLocation';
import { resolveEffectiveCompanyHours } from '../../locations/resolveLocationHours';
import {
  generateStorefrontTimeSlots,
  isStorefrontBookingDebugEnabled,
  logStorefrontBooking,
  summarizeSlotDiagnostics,
  traceStorefrontBooking,
  warnStorefrontBooking,
} from '../storefront-booking/generateStorefrontTimeSlots';
import {
  createInitialCalendarMonth,
  useStorefrontCalendarAvailability,
} from '../storefront-booking/calendar-availability/useStorefrontCalendarAvailability';

/** Logo URL for storefront "Any Available" — matches Header / portal patterns */
const resolveStorefrontCompanyLogoUrl = (companyData = {}) =>
  companyData.logoUrl ||
  companyData.logo ||
  companyData.logoURL ||
  companyData.imageUrl ||
  companyData.branding?.logoUrl ||
  companyData.settings?.logoUrl ||
  null;

const normalizeStorefrontPhotoURL = (value) => {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
};

const normalizeStorefrontAddon = (addon = {}) => ({
  id: addon.id || '',
  name: addon.name || '',
  description: addon.description || '',
  price: Number(addon.price) || 0,
  duration: Number(addon.duration) || 0,
  bufferTime: Number(addon.bufferTime) || 0,
});

const normalizeStorefrontPackage = (pkg) => {
  if (!pkg) return null;

  return {
    id: pkg.id || '',
    name: pkg.name || '',
    description: pkg.description || '',
    price: Number(pkg.price) || 0,
    duration: Number(pkg.duration) || 0,
    bufferTime: Number(pkg.bufferTime) || 0,
  };
};

const BookingFlow = ({ companyData, selectedService, onBookingComplete, onCancel }) => {
  const customerNoStaffMessage = 'This service is temporarily unavailable online right now. Please choose another service or contact us for help.';
  const {
    storefrontClient,
    sessionUser,
    hasPortalAccess,
    loading: authLoading
  } = useStorefrontAuth();
  const location = useLocation();
  const { companyUrl, serviceSlug } = useParams();
  const isBookingWithPresetService = Boolean(selectedService?.id);
  const { activeLocations, defaultLocation, showLocationStep, loading: locationsLoading } = useCompanyLocations({
    companyId: companyData?.id,
    companyData,
    activeOnly: true,
  });
  const [selectedLocation, setSelectedLocation] = useState(null);
  const bookingLocation = selectedLocation || (!showLocationStep ? defaultLocation : null);
  const getStepIndex = React.useCallback((name) => {
    const withLocation = {
      location: 0,
      service: 1,
      date: 2,
      time: 3,
      staff: 4,
      customer: 5,
      payment: 6,
    };
    const withoutLocation = {
      service: 0,
      date: 1,
      time: 2,
      staff: 3,
      customer: 4,
      payment: 5,
    };
    return (showLocationStep ? withLocation : withoutLocation)[name];
  }, [showLocationStep]);
  const effectiveCompanyHours = useMemo(
    () => resolveEffectiveCompanyHours({
      location: bookingLocation,
      companyData,
      locationId: bookingLocation?.id,
    }),
    [bookingLocation, companyData]
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Booking state
  const [service, setService] = useState(selectedService);
  const [staff, setStaff] = useState(null);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsFetched, setSlotsFetched] = useState(false);
  const [dayAppointments, setDayAppointments] = useState([]);
  const [addons, setAddons] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [storefrontLoginOpen, setStorefrontLoginOpen] = useState(false);
  const [availabilityVersion, setAvailabilityVersion] = useState(0);
  const slotsCacheKeyRef = useRef(null);
  const staffCacheKeyRef = useRef(null);
  const appointmentsCacheKeyRef = useRef(null);
  const desiredStaffCacheKeyRef = useRef(null);
  const desiredAppointmentsCacheKeyRef = useRef(null);
  const previousLocationIdRef = useRef(undefined);
  const isSignedInClient = Boolean(storefrontClient && hasPortalAccess);

  const enabledPaymentGateways = useMemo(
    () => resolveStorefrontPaymentGateways(companyData),
    [companyData]
  );
  const bookingTotalPrice = useMemo(
    () => computeStorefrontBookingTotal(service, selectedPackage, selectedAddons),
    [service, selectedPackage, selectedAddons]
  );
  const needsPaymentStep = Boolean(
    bookingTotalPrice > 0.01 && storefrontHasOnlineCheckout(enabledPaymentGateways)
  );
  const openStorefrontLoginModal = () => setStorefrontLoginOpen(true);

  const bumpAvailabilityVersion = () => {
    setAvailabilityVersion((version) => version + 1);
  };

  useEffect(() => {
    traceStorefrontBooking('bookingFlow.mounted', {
      companyId: companyData?.id,
      serviceId: service?.id || selectedService?.id,
      showLocationStep,
      bookingLocationId: bookingLocation?.id,
      viewTrace: 'dumpStorefrontBookingTrace()',
    });
  }, [companyData?.id, service?.id, selectedService?.id, showLocationStep, bookingLocation?.id]);

  useEffect(() => {
    setService(selectedService ?? null);
  }, [selectedService]);

  useEffect(() => {
    if (locationsLoading) return;

    if (!showLocationStep && defaultLocation && !selectedLocation) {
      setSelectedLocation(defaultLocation);
    }
  }, [locationsLoading, showLocationStep, defaultLocation, selectedLocation]);

  useEffect(() => {
    if (locationsLoading) return;
    if (!selectedService?.id) return;
    setCurrentStep(showLocationStep ? getStepIndex('service') : getStepIndex('date'));
  }, [selectedService?.id, locationsLoading, showLocationStep, getStepIndex]);

  useEffect(() => {
    if (!selectedService?.id || locationsLoading) return;
    if (showLocationStep && !selectedLocation) {
      setCurrentStep(getStepIndex('location'));
    }
  }, [selectedService?.id, locationsLoading, showLocationStep, selectedLocation, getStepIndex]);

  useEffect(() => {
    if (!isSignedInClient) {
      return;
    }

    setCustomerInfo((currentInfo) => ({
      ...currentInfo,
      name: storefrontClient.name || currentInfo.name,
      email: storefrontClient.email || currentInfo.email,
      phone: storefrontClient.phone || currentInfo.phone
    }));
  }, [isSignedInClient, storefrontClient]);

  useEffect(() => {
    if (!isSignedInClient || currentStep !== getStepIndex('staff')) return;
    setCurrentStep(needsPaymentStep ? getStepIndex('payment') : getStepIndex('customer'));
  }, [isSignedInClient, currentStep, needsPaymentStep, getStepIndex]);

  const getTotalDurationMinutes = () =>
    computeStorefrontTotalDurationMinutes(service, selectedPackage, selectedAddons);

  const doubleBookingSettings = useMemo(
    () => getDoubleBookingSettings(companyData),
    [companyData]
  );

  const slotsCacheKey = useMemo(() => {
    if (!date || !service?.id || !companyData?.id) return null;
    const addonKey = selectedAddons
      .map((a) => a.id)
      .sort()
      .join(',');
    return [
      companyData.id,
      service.id,
      format(date, 'yyyy-MM-dd'),
      selectedPackage?.id || '',
      addonKey,
      getTotalDurationMinutes(),
      bookingLocation?.id || '',
    ].join('|');
  }, [date, service, companyData?.id, selectedPackage, selectedAddons, bookingLocation?.id]);

  const staffCacheKey = useMemo(
    () => buildStorefrontStaffCacheKey({
      date,
      service,
      selectedPackage,
      selectedAddons,
      locationId: bookingLocation?.id,
    }),
    [date, service, selectedPackage, selectedAddons, bookingLocation?.id]
  );

  const availableStaffForSelectedSlot = useMemo(() => {
    const logoUrl = normalizeStorefrontPhotoURL(resolveStorefrontCompanyLogoUrl(companyData));
    const anyFromList = availableStaff.find((m) => m.isAnyAvailable);
    const anyOption = anyFromList
      ? {
          ...anyFromList,
          photoURL: normalizeStorefrontPhotoURL(anyFromList.photoURL) || logoUrl,
        }
      : {
          id: 'any',
          firstName: 'Any',
          lastName: 'Available',
          name: 'Any Available',
          isAnyAvailable: true,
          photoURL: logoUrl,
        };

    if (!time || !date || !availableStaff.length) {
      return availableStaff;
    }
    const companyHours = effectiveCompanyHours;
    const bookingDuration = getTotalDurationMinutes();
    const slotEnd = addMinutes(time, bookingDuration);
    const qualified = availableStaff.filter((member) => {
      if (member.isAnyAvailable) return false;
      if (
        !slotFitsEmployeeSchedule({
          workingSchedule: member.workingSchedule,
          date,
          slotStart: time,
          slotEnd,
          companyHours,
        })
      ) {
        return false;
      }
      return isEmployeeSlotAvailable({
        appointments: dayAppointments,
        employeeId: member.id,
        slotStart: time,
        slotEnd,
        ...doubleBookingSettings,
      });
    });
    if (!qualified.length) {
      return availableStaff;
    }
    const anyStillValid = isAnyEmployeeSlotAvailable({
      appointments: dayAppointments,
      employeeIds: qualified.map((m) => m.id),
      slotStart: time,
      slotEnd,
      ...doubleBookingSettings,
    });
    if (!anyStillValid) {
      return qualified;
    }
    return [anyOption, ...qualified];
  }, [
    time,
    date,
    dayAppointments,
    doubleBookingSettings,
    availableStaff,
    companyData,
    service,
    selectedPackage,
    selectedAddons,
    effectiveCompanyHours,
  ]);

  // Extract addons and packages from the service data
  useEffect(() => {
    if (!service) return;
    
    // Addons are stored as an array field 'addOns' in the service document
    const addonsData = (service.addOns || [])
      .filter(addon => addon.active !== false);
    setAddons(addonsData);
    
    // Packages are stored as an array field 'packages' in the service document
    const packagesData = (service.packages || [])
      .filter(pkg => pkg.active !== false);
    setPackages(packagesData);
  }, [service]);

  // Warm availability cache as soon as the booking flow opens with a preset service.
  useEffect(() => {
    if (!selectedService?.id || !companyData?.id) {
      return;
    }

    void prefetchStorefrontAvailabilityForToday({
      companyData,
      service: selectedService,
      selectedPackage,
      selectedAddons,
      customerNoStaffMessage,
      locationId: bookingLocation?.id,
      location: bookingLocation,
    });
  }, [selectedService?.id, companyData?.id, customerNoStaffMessage, bookingLocation?.id]);

  useEffect(() => {
    desiredStaffCacheKeyRef.current = staffCacheKey;
  }, [staffCacheKey]);

  useEffect(() => {
    if (!date || !companyData?.id) {
      desiredAppointmentsCacheKeyRef.current = null;
      return;
    }
    desiredAppointmentsCacheKeyRef.current = buildStorefrontDayAppointmentsKey(companyData.id, date);
  }, [date, companyData?.id]);

  // Prefetch staff + busy slots as soon as a date is selected
  useEffect(() => {
    if (!date || !service || !companyData) {
      return;
    }

    if (showLocationStep && !bookingLocation?.id) {
      traceStorefrontBooking('prefetch.skipped.noLocation', {
        showLocationStep,
      });
      return;
    }

    void warmStorefrontBusySlotsForDate(companyData.id, date);

    const dayAppointmentsKey = buildStorefrontDayAppointmentsKey(companyData.id, date);
    const needsStaff = staffCacheKeyRef.current !== staffCacheKey;
    const needsAppointments = appointmentsCacheKeyRef.current !== dayAppointmentsKey;

    if (!needsStaff && !needsAppointments) {
      return;
    }

    traceStorefrontBooking('prefetch.started', {
      date: format(date, 'yyyy-MM-dd'),
      locationId: bookingLocation?.id,
      serviceId: service?.id,
      staffCacheKey,
      needsStaff,
      needsAppointments,
    });

    const expectedStaffKey = staffCacheKey;
    const expectedAppointmentsKey = dayAppointmentsKey;

    const cachedStaff = needsStaff ? getCachedStorefrontStaffPrefetch(staffCacheKey) : null;
    if (cachedStaff) {
      if (cachedStaff.error) {
        setStaff(null);
        setAvailableStaff([]);
        setError(cachedStaff.error);
        staffCacheKeyRef.current = null;
      } else {
        setAvailableStaff(cachedStaff.staff);
        staffCacheKeyRef.current = staffCacheKey;
        setError(null);
      }
      bumpAvailabilityVersion();
      traceStorefrontBooking('prefetch.staffCacheHit', {
        staffCacheKey,
        rosterCount: (cachedStaff.staff || []).filter((member) => !member.isAnyAvailable).length,
      });
    }

    const prefetchAvailability = async () => {
      if (!cachedStaff) {
        setSlotsLoading(true);
        setSlotsFetched(false);
      }

      try {
        if (needsStaff && !cachedStaff) {
          setError(null);
        }

        const result = await prefetchStorefrontAvailability({
          companyData,
          service,
          date,
          selectedPackage,
          selectedAddons,
          customerNoStaffMessage,
          locationId: bookingLocation?.id,
          location: bookingLocation,
        });

        const staffStillDesired = desiredStaffCacheKeyRef.current === expectedStaffKey;
        const appointmentsStillDesired = desiredAppointmentsCacheKeyRef.current === expectedAppointmentsKey;

        if (!staffStillDesired && !appointmentsStillDesired) {
          traceStorefrontBooking('prefetch.staleResultIgnored', {
            expectedStaffKey,
            expectedAppointmentsKey,
            desiredStaffKey: desiredStaffCacheKeyRef.current,
            desiredAppointmentsKey: desiredAppointmentsCacheKeyRef.current,
          });
          return;
        }

        let availabilityChanged = false;

        if (needsStaff && staffStillDesired) {
          if (result.error) {
            setStaff(null);
            setAvailableStaff([]);
            setError(result.error);
            staffCacheKeyRef.current = null;
            availabilityChanged = true;
            warnStorefrontBooking('Staff prefetch returned an error', {
              error: result.error,
              locationId: bookingLocation?.id,
              serviceId: service?.id,
              debug: result.staffDebug,
            });
            setSlotsLoading(false);
            setSlotsFetched(true);
          } else {
            setAvailableStaff(result.staff);
            staffCacheKeyRef.current = expectedStaffKey;
            availabilityChanged = true;
            traceStorefrontBooking('prefetch.staffComplete', {
              locationId: bookingLocation?.id,
              serviceId: service?.id,
              rosterCount: (result.staff || []).filter((member) => !member.isAnyAvailable).length,
              staffDebug: result.staffDebug,
            });
          }
        }

        if (needsAppointments && appointmentsStillDesired) {
          setDayAppointments(result.appointments);
          appointmentsCacheKeyRef.current = expectedAppointmentsKey;
          availabilityChanged = true;
          traceStorefrontBooking('prefetch.appointmentsComplete', {
            date: format(date, 'yyyy-MM-dd'),
            appointmentCount: result.appointments?.length || 0,
          });
        }

        if (availabilityChanged) {
          bumpAvailabilityVersion();
        }
      } catch (err) {
        console.error('Error prefetching availability:', err);
        traceStorefrontBooking('prefetch.failed', {
          message: err?.message,
          code: err?.code,
          expectedStaffKey,
          expectedAppointmentsKey,
        });
        if (desiredStaffCacheKeyRef.current !== expectedStaffKey) {
          return;
        }
        setStaff(null);
        setAvailableStaff([]);
        setDayAppointments([]);
        staffCacheKeyRef.current = null;
        if (date && companyData?.id) {
          appointmentsCacheKeyRef.current = buildStorefrontDayAppointmentsKey(companyData.id, date);
        }
        setSlotsLoading(false);
        setSlotsFetched(true);
        setError('We could not load availability right now. Please try again.');
      }
    };

    void prefetchAvailability();
  }, [
    date,
    service,
    companyData,
    staffCacheKey,
    selectedAddons,
    selectedPackage,
    customerNoStaffMessage,
    bookingLocation?.id,
    showLocationStep,
  ]);

  useEffect(() => {
    const nextLocationId = selectedLocation?.id || null;
    const previousLocationId = previousLocationIdRef.current;

    if (previousLocationId === undefined) {
      previousLocationIdRef.current = nextLocationId;
      return;
    }

    if (previousLocationId === nextLocationId) {
      return;
    }

    previousLocationIdRef.current = nextLocationId;

    // Initial default-location assignment — keep prefetched availability.
    if (!previousLocationId && nextLocationId) {
      return;
    }

    staffCacheKeyRef.current = null;
    slotsCacheKeyRef.current = null;
    appointmentsCacheKeyRef.current = null;
    setAvailableSlots([]);
    setAvailableStaff([]);
    setDayAppointments([]);
    setStaff(null);
    setTime(null);
    setError(null);
  }, [selectedLocation?.id]);

  // Generate time slots once staff roster and day appointments are ready
  useEffect(() => {
    if (!date || !service || !companyData) {
      return;
    }

    if (showLocationStep && !bookingLocation?.id) {
      traceStorefrontBooking('slotGenerationSkipped.noLocation', {
        date: format(date, 'yyyy-MM-dd'),
      });
      return;
    }

    const roster = (availableStaff || []).filter((member) => !member.isAnyAvailable);
    const dayAppointmentsKey = buildStorefrontDayAppointmentsKey(companyData.id, date);
    const staffReady = staffCacheKeyRef.current === staffCacheKey;
    const appointmentsReady = appointmentsCacheKeyRef.current === dayAppointmentsKey;

    traceStorefrontBooking('slotGeneration.attempt', {
      date: format(date, 'yyyy-MM-dd'),
      locationId: bookingLocation?.id,
      rosterCount: roster.length,
      staffReady,
      appointmentsReady,
      currentStep,
      slotsCacheKey,
      cachedSlotsKey: slotsCacheKeyRef.current,
      availabilityVersion,
    });

    if (!roster.length) {
      if (error || staffReady) {
        if (staffReady && !error) {
          warnStorefrontBooking('No bookable staff for this service/location/date', {
            locationId: bookingLocation?.id,
            locationName: bookingLocation?.name,
            serviceId: service?.id,
            serviceName: service?.name,
            date: format(date, 'yyyy-MM-dd'),
            staffDebug: isStorefrontBookingDebugEnabled() ? availableStaff : undefined,
          });
        }
        setSlotsLoading(false);
        setSlotsFetched(true);
      } else {
        setSlotsLoading(true);
        setSlotsFetched(false);
      }
      logStorefrontBooking('slotGenerationSkipped', {
        reason: 'EMPTY_ROSTER',
        staffReady,
        error,
        locationId: bookingLocation?.id,
        serviceId: service?.id,
      });
      traceStorefrontBooking('slotGenerationSkipped.emptyRoster', {
        staffReady,
        error,
        locationId: bookingLocation?.id,
        serviceId: service?.id,
        date: format(date, 'yyyy-MM-dd'),
      });
      return;
    }

    if (!appointmentsReady) {
      setSlotsLoading(true);
      setSlotsFetched(false);
      traceStorefrontBooking('slotGenerationSkipped.appointmentsNotReady', {
        dayAppointmentsKey,
        appointmentsCacheKey: appointmentsCacheKeyRef.current,
        rosterCount: roster.length,
      });
      return;
    }

    if (slotsCacheKey && slotsCacheKeyRef.current === slotsCacheKey) {
      setSlotsLoading(false);
      setSlotsFetched(true);
      traceStorefrontBooking('slotGenerationSkipped.cacheHit', {
        slotsCacheKey,
      });
      return;
    }

    try {
      setSlotsLoading(true);

      const totalDuration = getTotalDurationMinutes();
      const bookingConflictSettings = getDoubleBookingSettings(companyData);
      const { slots, diagnostics } = generateStorefrontTimeSlots({
        date,
        companyHours: effectiveCompanyHours,
        totalDuration,
        rosterForSlots: roster,
        appointmentsForDay: dayAppointments,
        bookingConflictSettings,
        staff,
      });

      if (slots.length === 0) {
        warnStorefrontBooking('No available time slots generated', summarizeSlotDiagnostics({
          ...diagnostics,
          locationId: bookingLocation?.id,
          locationName: bookingLocation?.name,
          serviceId: service?.id,
          serviceName: service?.name,
          doubleBookingSettings: bookingConflictSettings,
        }));
      } else {
        traceStorefrontBooking('slotsGenerated', {
          count: slots.length,
          firstSlot: format(slots[0], 'HH:mm'),
          locationId: bookingLocation?.id,
          serviceId: service?.id,
          blockReason: null,
        });
      }

      startTransition(() => {
        setAvailableSlots(slots);
      });
    } catch (err) {
      console.error('Error generating time slots:', err);
      traceStorefrontBooking('slotGeneration.failed', {
        message: err?.message,
        code: err?.code,
      });
      setAvailableSlots([]);
      setError(
        'We could not load available times right now. Please try again.'
      );
      setDayAppointments([]);
      appointmentsCacheKeyRef.current = null;
    } finally {
      setSlotsLoading(false);
      setSlotsFetched(true);
      slotsCacheKeyRef.current = slotsCacheKey;
    }
  }, [
    date,
    service,
    companyData,
    availableStaff,
    dayAppointments,
    slotsCacheKey,
    staff,
    error,
    selectedAddons,
    selectedPackage,
    effectiveCompanyHours,
    bookingLocation?.id,
    staffCacheKey,
    showLocationStep,
    availabilityVersion,
    currentStep,
  ]);

  const handleConfirmBooking = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[StorefrontBooking] Starting booking confirmation', {
        companyId: companyData?.id,
        serviceId: service?.id,
        isSignedInClient,
        selectedStaffId: staff?.id,
        selectedDate: date,
        selectedTime: time
      });

      const bookingDuration = getTotalDurationMinutes();
      let chosenEmployee = staff?.isAnyAvailable
        ? availableStaff.find((employee) => (
            !employee.isAnyAvailable &&
            slotFitsEmployeeSchedule({
              workingSchedule: employee.workingSchedule,
              date,
              slotStart: time,
              slotEnd: addMinutes(time, bookingDuration),
              companyHours: effectiveCompanyHours,
            })
          ))
        : staff;

      if (!chosenEmployee) {
        throw new Error('No staff member is available for this service.');
      }

      const conflictSettings = getDoubleBookingSettings(companyData);
      const serviceDuration = selectedPackage?.duration || service.duration || 0;
      const serviceBuffer = selectedPackage?.duration
        ? (selectedPackage.bufferTime || 0)
        : (service.bufferTime || 0);

      if (staff?.isAnyAvailable) {
        const rosterIds = availableStaff
          .filter((member) => !member.isAnyAvailable)
          .map((member) => member.id);
        const { isAvailable, availableOptions } =
          await AvailabilityService.checkAnyEmployeeAvailability({
            companyId: companyData.id,
            employeeIds: rosterIds,
            startTime: time,
            duration: serviceDuration,
            bufferTime: serviceBuffer,
            addons: selectedAddons,
            ...conflictSettings,
          });

        if (!isAvailable || !availableOptions?.length) {
          throw new Error(
            'This time slot is no longer available. Please choose a different time.'
          );
        }

        const picked =
          availableOptions.find((opt) => opt.employeeId === chosenEmployee?.id) ||
          availableOptions[0];
        const resolvedStaff = availableStaff.find(
          (member) => member.id === picked.employeeId
        );
        if (resolvedStaff) {
          chosenEmployee = resolvedStaff;
        }
      } else {
        const { isAvailable } = await AvailabilityService.checkEmployeeAvailability({
          companyId: companyData.id,
          employeeId: chosenEmployee.id,
          startTime: time,
          duration: serviceDuration,
          bufferTime: serviceBuffer,
          addons: selectedAddons,
          ...conflictSettings,
        });

        if (!isAvailable) {
          throw new Error(
            'This time slot is no longer available for the selected staff member. Please choose a different time.'
          );
        }
      }

      console.log('[StorefrontBooking] Chosen employee resolved', {
        employeeId: chosenEmployee.id,
        employeeName: chosenEmployee.name,
        isOwner: chosenEmployee.isOwner || false
      });

      // Calculate total duration including addons
      let totalDuration = selectedPackage?.duration || service.duration || 0;
      if (selectedPackage?.duration) {
        totalDuration += selectedPackage.bufferTime || 0;
      }
      selectedAddons.forEach(addon => {
        totalDuration += addon.duration || 0;
        totalDuration += addon.bufferTime || 0;
      });

      const totalPrice = computeStorefrontBookingTotal(service, selectedPackage, selectedAddons);

      let bookingClientId;
      let bookingClientName;
      let bookingClientEmail;
      let bookingClientPhone;

      if (isSignedInClient) {
        const sessionUser = auth.currentUser;
        console.log('[StorefrontBooking] Signed-in client path', {
          authUid: sessionUser?.uid,
          authEmail: sessionUser?.email
        });

        if (!sessionUser) {
          throw new Error('Your session expired. Please sign in again to book under your client portal account.');
        }
        bookingClientId = storefrontClient?.id || sessionUser.uid;
        bookingClientName = customerInfo.name || storefrontClient?.name || '';
        bookingClientEmail = sessionUser.email || storefrontClient?.email || customerInfo.email;
        bookingClientPhone = customerInfo.phone || storefrontClient?.phone || '';
        console.log('[StorefrontBooking] Using existing storefront client identity', {
          bookingClientId,
          bookingClientEmail
        });
      } else {
        const [firstName, ...lastNameParts] = customerInfo.name.trim().split(/\s+/);
        const lastName = lastNameParts.join(' ');

        const customerData = {
          firstName,
          lastName,
          name: customerInfo.name,
          searchName: customerInfo.name.toLowerCase(),
          email: customerInfo.email,
          phone: customerInfo.phone,
          source: 'storefront',
          status: 'active',
          archived: false,
          createdAt: new Date().toISOString(),
          loyaltyPoints: 0,
          totalSpent: 0,
          appointmentCount: 0
        };

        const customerDoc = await createPublicClient({
          companyId: companyData.id,
          record: customerData,
        });
        console.log('[StorefrontBooking] Guest client record created', {
          customerId: customerDoc.id
        });
        bookingClientId = customerDoc.id;
        bookingClientName = customerInfo.name;
        bookingClientEmail = customerInfo.email;
        bookingClientPhone = customerInfo.phone;
      }

      const appointmentData = {
        companyId: companyData.id,
        clientId: bookingClientId,
        clientName: bookingClientName,
        clientEmail: bookingClientEmail,
        clientPhone: bookingClientPhone,
        dateTime: time,
        endTime: addMinutes(time, totalDuration + (service.bufferTime || 0)),
        duration: totalDuration + (service.bufferTime || 0),
        serviceId: service.id,
        serviceName: service.name,
        servicePrice: service.price,
        employee: {
          id: chosenEmployee.id,
          name: chosenEmployee.name,
          title: chosenEmployee.title || (chosenEmployee.isOwner ? 'Owner' : 'Staff Member'),
          isOwner: chosenEmployee.isOwner || false,
        },
        employeeId: chosenEmployee.id,
        employeeName: chosenEmployee.name,
        employeeTitle: chosenEmployee.title || (chosenEmployee.isOwner ? 'Owner' : 'Staff Member'),
        employeeIsOwner: chosenEmployee.isOwner || false,
        staffId: chosenEmployee.id,
        source: 'storefront',
        bookingType: 'storefront',
        status: isSignedInClient
          ? getDefaultAppointmentStatus(companyData?.settings?.bookingSettings)
          : 'pending',
        createdAt: new Date().toISOString(),
        totalPrice: totalPrice,
        paymentStatus: 'unpaid',
        selectedAddons: selectedAddons.map(normalizeStorefrontAddon),
        selectedPackage: normalizeStorefrontPackage(selectedPackage),
        ...buildAppointmentLocationFields(selectedLocation || defaultLocation),
        publicShare: true,
      };

      console.log('[StorefrontBooking] Creating appointment', {
        clientId: bookingClientId,
        companyId: companyData.id,
        bookingType: appointmentData.bookingType,
        status: appointmentData.status,
        addonCount: appointmentData.selectedAddons.length,
        hasPackage: Boolean(appointmentData.selectedPackage)
      });
      const createdAppointment = await createStorefrontAppointment({
        companyId: companyData.id,
        appointment: appointmentData,
      });
      const appointmentId = createdAppointment?.id;
      const publicNumber =
        createdAppointment?.publicNumber != null ? Number(createdAppointment.publicNumber) : null;
      console.log('[StorefrontBooking] Appointment created successfully', { appointmentId, publicNumber });

      onBookingComplete?.({
        appointmentId,
        publicNumber,
        summary: {
          serviceName: service.name,
          selectedPackage: normalizeStorefrontPackage(selectedPackage),
          selectedAddons: selectedAddons.map(normalizeStorefrontAddon),
          duration: appointmentData.duration,
          dateTime: time.toISOString(),
          employeeName: chosenEmployee.name,
          totalPrice,
          paymentStatus: appointmentData.paymentStatus,
          publicNumber,
        },
      });
    } catch (err) {
      console.error('Error creating booking:', {
        code: err?.code,
        message: err?.message,
        stack: err?.stack,
        raw: err
      });
      setError(err.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (nextDate) => {
    setDate(nextDate);
    setStaff(null);
    setTime(null);
    setAvailableSlots([]);
    setSlotsLoading(false);
    setSlotsFetched(false);
    slotsCacheKeyRef.current = null;
    setError(null);

    const nextStaffCacheKey = buildStorefrontStaffCacheKey({
      date: nextDate,
      service,
      selectedPackage,
      selectedAddons,
      locationId: bookingLocation?.id,
    });
    const cachedStaff = getCachedStorefrontStaffPrefetch(nextStaffCacheKey);

    if (cachedStaff) {
      if (cachedStaff.error) {
        setAvailableStaff([]);
        setError(cachedStaff.error);
        staffCacheKeyRef.current = null;
      } else {
        setAvailableStaff(cachedStaff.staff);
        staffCacheKeyRef.current = nextStaffCacheKey;
      }
      bumpAvailabilityVersion();
    } else {
      setAvailableStaff([]);
      staffCacheKeyRef.current = null;
    }

    setDayAppointments([]);
    appointmentsCacheKeyRef.current = null;
  };

  const handleDateStepNext = () => {
    const dayAppointmentsKey = date && companyData?.id
      ? buildStorefrontDayAppointmentsKey(companyData.id, date)
      : null;
    const rosterReady =
      Boolean(staffCacheKey) &&
      staffCacheKeyRef.current === staffCacheKey &&
      availableStaff.length > 0;
    const appointmentsReady =
      Boolean(dayAppointmentsKey) &&
      appointmentsCacheKeyRef.current === dayAppointmentsKey;

    if (!rosterReady || !appointmentsReady) {
      setSlotsLoading(true);
      setSlotsFetched(false);
    }

    setCurrentStep(getStepIndex('time'));
  };

  const renderStep = () => {
    if (showLocationStep && currentStep === getStepIndex('location')) {
      return (
        <div className="booking-step">
          <LocationPicker
            locations={activeLocations}
            selectedLocationId={selectedLocation?.id || ''}
            onSelect={(location) => setSelectedLocation(location)}
          />
          <div className="button-group">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Back
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!selectedLocation?.id}
              onClick={() => setCurrentStep(getStepIndex('service'))}
            >
              Continue
            </button>
          </div>
        </div>
      );
    }

    switch (currentStep) {
      case getStepIndex('service'):
        return (
          <ServiceSelectionStep
            service={service}
            addons={addons}
            packages={packages}
            selectedAddons={selectedAddons}
            setSelectedAddons={setSelectedAddons}
            selectedPackage={selectedPackage}
            setSelectedPackage={setSelectedPackage}
            onNext={() => setCurrentStep(getStepIndex('date'))}
            onCancel={onCancel}
            onBack={
              showLocationStep
                ? () => setCurrentStep(getStepIndex('location'))
                : onCancel
            }
          />
        );
      case getStepIndex('date'):
        return (
          <DateSelectionStep
            date={date}
            setDate={handleDateChange}
            onNext={handleDateStepNext}
            onBack={() => (
              isBookingWithPresetService && !showLocationStep
                ? onCancel()
                : setCurrentStep(getStepIndex('service'))
            )}
            companyData={companyData}
            service={service}
            selectedPackage={selectedPackage}
            selectedAddons={selectedAddons}
            companyHours={effectiveCompanyHours}
            bookingLocation={bookingLocation}
            customerNoStaffMessage={customerNoStaffMessage}
          />
        );
      case getStepIndex('time'):
        return (
          <TimeSelectionStep
            date={date}
            time={time}
            setTime={setTime}
            availableSlots={availableSlots}
            loading={slotsLoading}
            slotsFetched={slotsFetched}
            error={error}
            customerNoStaffMessage={customerNoStaffMessage}
            onNext={() => {
              setError(null);
              setCurrentStep(getStepIndex('staff'));
            }}
            onBack={() => setCurrentStep(getStepIndex('date'))}
            onCancel={onCancel}
          />
        );
      case getStepIndex('staff'):
        return (
          <StaffSelectionStep
            staff={staff}
            setStaff={setStaff}
            availableStaff={availableStaffForSelectedSlot}
            error={error}
            customerNoStaffMessage={customerNoStaffMessage}
            loading={loading}
            onNext={() => {
              setError(null);
              if (isSignedInClient) {
                if (needsPaymentStep) {
                  setCurrentStep(getStepIndex('payment'));
                } else {
                  void handleConfirmBooking();
                }
              } else {
                setCurrentStep(getStepIndex('customer'));
              }
            }}
            onBack={() => {
              setStaff(null);
              setCurrentStep(getStepIndex('time'));
            }}
            onCancel={onCancel}
          />
        );
      case getStepIndex('customer'):
        if (isSignedInClient) {
          return null;
        }
        return (
          <CustomerInfoStep
            customerInfo={customerInfo}
            setCustomerInfo={setCustomerInfo}
            companyData={companyData}
            service={service}
            selectedAddons={selectedAddons}
            selectedPackage={selectedPackage}
            willPayOnlineNext={needsPaymentStep}
            onConfirm={
              needsPaymentStep ? () => setCurrentStep(getStepIndex('payment')) : handleConfirmBooking
            }
            confirmLabel={needsPaymentStep ? 'Continue to payment' : 'Confirm Booking'}
            onBack={() => setCurrentStep(getStepIndex('staff'))}
            loading={loading}
            error={error}
          />
        );
      case getStepIndex('payment'):
        return (
          <StorefrontPaymentStep
            companyData={companyData}
            companyUrl={companyUrl}
            serviceSlug={serviceSlug}
            service={service}
            staff={staff}
            availableStaff={availableStaff}
            date={date}
            time={time}
            selectedAddons={selectedAddons}
            selectedPackage={selectedPackage}
            customerInfo={customerInfo}
            isSignedInClient={isSignedInClient}
            storefrontClient={storefrontClient}
            enabledGateways={enabledPaymentGateways}
            getTotalDurationMinutes={getTotalDurationMinutes}
            selectedLocation={selectedLocation}
            onBack={() => setCurrentStep(isSignedInClient ? getStepIndex('staff') : getStepIndex('customer'))}
          />
        );
      default:
        return null;
    }
  };

  const storefrontBannerContent = (() => {
    if (isSignedInClient) {
      return null;
    }
    if (sessionUser) {
      return (
        <>
          <div>
            <strong>{sessionUser.email}</strong> is signed in, but that account is not linked to
            this business&apos;s client portal. You can continue as a guest or switch accounts.
          </div>
          <button type="button" className="storefront-booking-banner__action" onClick={openStorefrontLoginModal}>
            Switch account
          </button>
        </>
      );
    }
    return (
      <>
        <div>
          Already have a client portal account? Sign in to book under the same account and keep
          everything synced.
        </div>
        <button
          type="button"
          className="storefront-booking-banner__action"
          onClick={openStorefrontLoginModal}
          disabled={authLoading}
        >
          Sign in
        </button>
      </>
    );
  })();

  return (
    <div className="booking-flow">
      {storefrontBannerContent ? (
        <div className="storefront-booking-banner">{storefrontBannerContent}</div>
      ) : null}
      {renderStep()}
      {storefrontLoginOpen ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => setStorefrontLoginOpen(false)}
        >
          <div
            className="modal-content storefront-login-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Client login"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header storefront-login-modal__header">
              <button
                type="button"
                className="close-button"
                aria-label="Close login"
                onClick={() => setStorefrontLoginOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body storefront-login-modal__body">
              <StorefrontLoginPanel
                companyData={companyData}
                companyUrl={companyUrl}
                returnPath={location.pathname}
                onAuthResolved={() => setStorefrontLoginOpen(false)}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const ServiceSelectionStep = ({
  service,
  addons,
  packages,
  selectedAddons,
  setSelectedAddons,
  selectedPackage,
  setSelectedPackage,
  onNext,
  onCancel,
  onBack,
}) => {
  const toggleAddon = (addon) => {
    if (selectedAddons.find(a => a.id === addon.id)) {
      setSelectedAddons(selectedAddons.filter(a => a.id !== addon.id));
    } else {
      setSelectedAddons([...selectedAddons, addon]);
    }
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(selectedPackage?.id === pkg.id ? null : pkg);
  };

  const calculateTotal = () => computeStorefrontBookingTotal(service, selectedPackage, selectedAddons);

  return (
    <div className="booking-step">
      {service ? (
        <>
          <div className="service-summary">
            <h4>{service.name}</h4>
            <p>{service.description}</p>
            <p>Duration: {service.duration} minutes</p>
            <p>Price: ${service.price}</p>
          </div>

          {packages.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h4 style={{ color: 'var(--page-text, #0f172a)', marginBottom: '10px' }}>Packages</h4>
              {packages.map(pkg => (
                <div
                  key={pkg.id}
                  onClick={() => handlePackageSelect(pkg)}
                  style={{
                    padding: '15px',
                    border: `2px solid ${selectedPackage?.id === pkg.id ? 'var(--page-accent, #1d4ed8)' : '#e0e0e0'}`,
                    borderRadius: '8px',
                    marginBottom: '10px',
                    cursor: 'pointer',
                    background: selectedPackage?.id === pkg.id ? 'var(--page-accent-soft, #eff6ff)' : 'white'
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '5px' }}>{pkg.name}</div>
                  {pkg.description && <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '5px' }}>{pkg.description}</div>}
                  <div style={{ fontWeight: 600, color: 'var(--page-accent, #1d4ed8)' }}>${pkg.price}</div>
                </div>
              ))}
            </div>
          )}

          {addons.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h4 style={{ color: 'var(--page-text, #0f172a)', marginBottom: '10px' }}>Add-ons</h4>
              {addons.map(addon => (
                <div
                  key={addon.id}
                  onClick={() => toggleAddon(addon)}
                  style={{
                    padding: '15px',
                    border: `2px solid ${selectedAddons.find(a => a.id === addon.id) ? 'var(--page-accent, #1d4ed8)' : '#e0e0e0'}`,
                    borderRadius: '8px',
                    marginBottom: '10px',
                    cursor: 'pointer',
                    background: selectedAddons.find(a => a.id === addon.id) ? 'var(--page-accent-soft, #eff6ff)' : 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>{addon.name}</div>
                    {addon.description && <div style={{ fontSize: '0.9rem', color: '#666' }}>{addon.description}</div>}
                  </div>
                  <div style={{ fontWeight: 600, color: 'var(--page-accent, #1d4ed8)' }}>+${addon.price}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '8px' }}>
            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>Total: ${calculateTotal()}</div>
          </div>

          <div className="button-group">
            <button type="button" className="btn-secondary" onClick={onBack || onCancel}>
              Back
            </button>
            <button type="button" className="btn-primary" onClick={onNext}>
              Continue
            </button>
          </div>
        </>
      ) : (
        <>
          <h3>Select a Service</h3>
          <p className="booking-emptyStateMessage">
            Choose a service from the storefront to continue booking.
          </p>
          <div className="button-group">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Back
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const StaffSelectionStep = ({
  staff,
  setStaff,
  availableStaff,
  error,
  customerNoStaffMessage,
  loading = false,
  onNext,
  onBack,
  onCancel,
}) => {
  if (error) {
    return (
      <div className="booking-step booking-step--staff">
        <h3>Choose Staff Member</h3>
        <div className="booking-emptyState">
          <p className="booking-emptyStateMessage">{error}</p>
          <div className="button-group">
            <button className="btn-secondary" onClick={onBack}>Back</button>
            {error === customerNoStaffMessage ? (
              <button className="btn-primary" onClick={onCancel}>Choose Another Service</button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-step booking-step--staff">
      <h3>Choose Staff Member</h3>
      <StorefrontStaffSelectionGrid
        employees={availableStaff}
        selectedStaff={staff}
        onSelect={setStaff}
      />
      <div className="button-group">
        <button type="button" className="btn-secondary" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onNext}
          disabled={!staff || loading}
        >
          {loading ? 'Confirming...' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

const DateSelectionStep = ({
  date,
  setDate,
  onNext,
  onBack,
  companyData,
  service,
  selectedPackage,
  selectedAddons,
  companyHours,
  bookingLocation,
  customerNoStaffMessage,
}) => {
  const minDate = useMemo(() => startOfDay(new Date()), []);
  const maxDate = useMemo(() => addYears(minDate, 5), [minDate]);
  const [calendarMonth, setCalendarMonth] = useState(() => createInitialCalendarMonth(date));

  const handleCalendarMonthChange = useCallback((nextMonth) => {
    setCalendarMonth((prev) => {
      if (format(startOfMonth(prev), 'yyyy-MM') === format(startOfMonth(nextMonth), 'yyyy-MM')) {
        return prev;
      }
      return startOfMonth(nextMonth);
    });
  }, []);

  const {
    isDateUnavailable,
    hasAvailabilityForDate,
    loading: availabilityLoading,
  } = useStorefrontCalendarAvailability({
    enabled: Boolean(companyData?.id && service?.id),
    companyData,
    service,
    selectedPackage,
    selectedAddons,
    companyHours,
    locationId: bookingLocation?.id ?? null,
    location: bookingLocation,
    customerNoStaffMessage,
    monthCursor: calendarMonth,
    minDate,
    maxDate,
  });

  useEffect(() => {
    if (date && isDateUnavailable(date)) {
      setDate(null);
    }
  }, [date, isDateUnavailable, setDate]);

  const canContinue =
    Boolean(date) && !availabilityLoading && hasAvailabilityForDate(date);

  return (
    <div className="booking-step booking-step--date">
      <h3>Select a Date</h3>

      <div className="date-picker date-picker--calendar">
        <StorefrontBookingCalendar
          value={date}
          onChange={setDate}
          minDate={minDate}
          maxDate={maxDate}
          isDateUnavailable={isDateUnavailable}
          availabilityLoading={availabilityLoading}
          onMonthChange={handleCalendarMonthChange}
        />
      </div>

      <div className="button-group">
        <button type="button" className="btn-secondary" onClick={onBack}>
          Back
        </button>
        <button type="button" className="btn-primary" onClick={onNext} disabled={!canContinue}>
          Continue
        </button>
      </div>
    </div>
  );
};

const formatMoney = (n) => {
  const num = Number(n) || 0;
  return num.toFixed(2);
};

const CustomerInfoStep = ({
  customerInfo,
  setCustomerInfo,
  companyData,
  service,
  selectedAddons,
  selectedPackage,
  willPayOnlineNext = false,
  onConfirm,
  confirmLabel = 'Confirm Booking',
  onBack,
  loading,
  error,
}) => {
  const { settings: depositSettings, calculateServiceDeposit } = useDepositSettings(companyData);

  const serviceForDeposit = useMemo(
    () =>
      service
        ? {
            ...service,
            selectedPackage,
            selectedAddons,
          }
        : null,
    [service, selectedPackage, selectedAddons]
  );

  const depositAmountRaw = useMemo(() => {
    if (!serviceForDeposit || !depositSettings?.globalEnabled) {
      return 0;
    }
    try {
      const amount = calculateServiceDeposit(serviceForDeposit);
      return amount > 0 ? amount : 0;
    } catch (e) {
      console.error('[StorefrontBooking] deposit calculation failed', e);
      return 0;
    }
  }, [serviceForDeposit, depositSettings, calculateServiceDeposit]);

  const appointmentTotal = useMemo(
    () => computeStorefrontBookingTotal(service, selectedPackage, selectedAddons),
    [service, selectedPackage, selectedAddons]
  );

  const depositDue = useMemo(() => {
    if (!depositSettings?.globalEnabled || depositAmountRaw <= 0) {
      return 0;
    }
    return Math.min(depositAmountRaw, appointmentTotal);
  }, [depositSettings, depositAmountRaw, appointmentTotal]);

  const payDeposit = willPayOnlineNext && depositDue > 0.01;

  return (
    <div className="booking-step booking-step--customer-info">
      <h3>Your Information</h3>

      <div className="form-group">
        <label>Full Name</label>
        <input
          type="text"
          value={customerInfo.name}
          onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
          placeholder="John Doe"
          required
        />
      </div>

      <div className="form-group">
        <label>Email</label>
        <input
          type="email"
          value={customerInfo.email}
          onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
          placeholder="john@example.com"
          required
        />
      </div>

      <div className="form-group">
        <label>Phone</label>
        <input
          type="tel"
          value={customerInfo.phone}
          onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
          placeholder="(555) 123-4567"
          required
        />
      </div>

      {payDeposit ? (
        <p className="storefront-payment-step__intro storefront-guest-deposit-notice">
          On the next step, you&apos;ll pay a{' '}
          <strong>${formatMoney(depositDue)}</strong> deposit securely online to confirm your
          appointment. The remaining balance will be handled according to the business&apos;s
          policy.
        </p>
      ) : null}

      {payDeposit && hasCancellationPolicyContent(depositSettings?.cancellationPolicy) ? (
        <section
          className="storefront-guest-policy-section"
          aria-label="Cancellation and refund policy"
        >
          <DepositCancellationPolicy cancellationPolicy={depositSettings.cancellationPolicy} />
        </section>
      ) : null}

      {error && <div className="error-message">{error}</div>}

      <div className="button-group">
        <button className="btn-secondary" onClick={onBack}>Back</button>
        <button
          className="btn-primary"
          onClick={onConfirm}
          disabled={loading || !customerInfo.name || !customerInfo.email || !customerInfo.phone}
        >
          {loading ? 'Confirming...' : confirmLabel}
        </button>
      </div>
    </div>
  );
};

export default BookingFlow;






